import React from "react";
import { Link } from "react-router-dom";
import { Check, Loader2 } from "lucide-react";

const PHASE_COLORS = {
  PLAN: "bg-blue-100 text-blue-700",
  DO: "bg-teal-100 text-teal-700",
  CHECK: "bg-amber-100 text-amber-700",
  ACT: "bg-purple-100 text-purple-700",
};

/**
 * Reusable task queue component for displaying numbered task lists with progress tracking.
 *
 * @param {Object} props
 * @param {string} props.title - Header title (e.g., "My Program To-Do List")
 * @param {Array} props.tasks - Array of task objects: { id, title, description, link, icon, phase, time, isComplete }
 * @param {boolean} [props.loading] - Show loading spinner
 * @param {string} [props.completeTitle] - Title shown when all tasks are done
 * @param {string} [props.completeDescription] - Description when all tasks are done
 * @param {string} [props.testId] - data-testid for the root element
 */
export default function TaskQueue({
  title = "Task Queue",
  tasks = [],
  loading = false,
  completeTitle = "All Tasks Complete!",
  completeDescription = "All tasks have been completed.",
  testId = "task-queue",
}) {
  const completedCount = tasks.filter((t) => t.isComplete).length;
  const totalTasks = tasks.length;
  const progress = totalTasks > 0 ? Math.round((completedCount / totalTasks) * 100) : 0;
  const firstIncomplete = tasks.find((t) => !t.isComplete);

  if (loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm" data-testid={testId}>
        <div className="flex items-center justify-center gap-2 py-8">
          <Loader2 className="w-5 h-5 animate-spin text-sky-700" />
          <span className="text-sm text-slate-500">Loading tasks...</span>
        </div>
      </div>
    );
  }

  if (totalTasks === 0) return null;

  if (completedCount === totalTasks) {
    return (
      <div className="bg-white border border-teal-200 rounded-xl shadow-sm p-4" data-testid={testId}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
            <Check className="w-5 h-5 text-teal-600" />
          </div>
          <div>
            <p className="font-semibold text-teal-800 text-sm" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{completeTitle}</p>
            <p className="text-xs text-teal-600">{completeDescription}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid={testId}>
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-200">
        <h3 className="text-sm font-bold text-slate-800" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{title}</h3>
        <span className="text-xs text-slate-500">{completedCount} of {totalTasks} tasks complete</span>
      </div>

      {/* Progress Bar */}
      <div className="flex items-center gap-3 px-5 py-3 bg-slate-50 border-b border-slate-200">
        <div className="flex-1 h-1.5 bg-slate-200 rounded-full overflow-hidden">
          <div className="h-full bg-teal-500 rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
        </div>
        <span className="text-xs font-medium text-slate-500 whitespace-nowrap">{progress}% complete</span>
      </div>

      {/* Task List */}
      <ul className="divide-y divide-slate-100">
        {tasks.map((task, index) => {
          const isNext = !task.isComplete && task === firstIncomplete;
          const Icon = task.icon;

          return (
            <li
              key={task.id}
              className={`flex items-start gap-3 px-5 py-3.5 transition-colors ${
                task.isComplete ? "opacity-60" : isNext ? "bg-sky-50 border-l-[3px] border-l-sky-700" : "hover:bg-slate-50"
              }`}
              data-testid={`task-${task.id}`}
            >
              {/* Number Badge */}
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5 ${
                  task.isComplete
                    ? "bg-teal-100 text-teal-600"
                    : isNext
                    ? "bg-sky-700 text-white animate-pulse"
                    : "bg-slate-200 text-slate-500"
                }`}
              >
                {task.isComplete ? <Check className="w-3.5 h-3.5" /> : index + 1}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-semibold ${task.isComplete ? "line-through text-slate-500" : "text-slate-800"}`}>{task.title}</p>
                <p className="text-xs text-slate-500 mt-0.5">{task.description}</p>
                <div className="flex items-center gap-2 mt-1.5">
                  {task.phase && (
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${PHASE_COLORS[task.phase] || "bg-gray-100 text-gray-600"}`}>
                      {task.phase}
                    </span>
                  )}
                  {task.time && <span className="text-[10px] text-slate-400">{task.time}</span>}
                </div>
              </div>

              {/* CTA */}
              {task.link ? (
                <Link to={task.link} className="flex-shrink-0 mt-0.5">
                  <button
                    className={`text-xs font-semibold px-3.5 py-1.5 rounded-lg transition-colors ${
                      task.isComplete
                        ? "bg-teal-100 text-teal-600 cursor-default"
                        : isNext
                        ? "bg-sky-700 hover:bg-sky-800 text-white"
                        : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                    }`}
                    data-testid={`task-cta-${task.id}`}
                  >
                    {task.isComplete ? "Done" : isNext ? "Continue" : "Begin"}
                  </button>
                </Link>
              ) : task.onClick ? (
                <button
                  onClick={task.onClick}
                  className={`flex-shrink-0 mt-0.5 text-xs font-semibold px-3.5 py-1.5 rounded-lg transition-colors ${
                    task.isComplete
                      ? "bg-teal-100 text-teal-600 cursor-default"
                      : isNext
                      ? "bg-sky-700 hover:bg-sky-800 text-white"
                      : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                  }`}
                  data-testid={`task-cta-${task.id}`}
                >
                  {task.isComplete ? "Done" : isNext ? "Continue" : "Begin"}
                </button>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
